import { useState } from 'react'
import { useNavigate } from 'react-router'
import { GripVertical, IdCard } from 'lucide-react'
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core'
import type { DragEndEvent } from '@dnd-kit/core'
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { quotaTiers as api } from '@/api/endpoints'
import type { QuotaTier } from '@/api/types'
import { Err } from '@/components/Err'
import { ToneBadge } from '@/components/ToneBadge'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Empty, PageHead, Section, SectionCrumb } from '@/components/layout/Page'
import { usePoll } from '@/hooks/usePoll'
import { limitAxes, limitsText } from '@/domain/quota'
import { DASH } from '@/ui/format'
import { ACTION, ACTION_DANGER, ACTION_PLAIN } from './vm/chips'
import { matchAny } from './model'
import type { World } from './model'
import { QuotaPanel } from './user/QuotaPanel'
import { TierCreateForm, TierDeleteForm } from './QuotaTierForms'

const POLL_MS = 30000

// `직위`(F-72) — 쿼터를 가진 이름. 소속은 팀, 권한은 등급, 그리고 남은 축이 이것이다.
//
// **적는 것은 이름 하나다**(#524). 식별자는 uuid 이고 화면에 나오지 않는다 — 쓰는 사람이
// 두 번째 이름을 지어낼 일이 없고, 그래서 한글로 적을 수 있다. 같은 이름의 직위 둘도 막지 않는다:
// 수정·기본 지정·적용이 모두 id 를 보내므로 이름이 모호해질 자리가 없다.
//
// 한도는 축마다 칸이다(VM·CPU·메모리·디스크) — 한 줄로 이어 쓰면 직위끼리 같은 축을 견주기 어렵다.
// 좁은 화면에서는 표의 머리가 접혀 머리 없는 숫자가 무엇의 몇인지 말하지 못하므로, 칸을 숨기고
// 이름 아래에 축 이름을 값이 지고 가는 한 줄(`domain/quota.ts` 의 `limitsText`)을 둔다.
//
// 조작은 팀·사용자 화면처럼 제목 줄의 칩이다 — `직위 생성` 과 고른 직위의 `지우기`. 줄을 누르면 상세
// (`QuotaTierView.tsx`)로 가고, 수정·기본 지정·사람 넣기는 거기 있다. **기본 직위는 고를 수 없다** —
// 새 계정이 들어가는 자리라 서버가 거절할 것을 화면이 먼저 안다. 사람이 든 직위는 고를 수 있고
// 지우기 창이 그 줄에 막힘을 말한다(`QuotaTierForms.tsx`).
//
// **순서는 손잡이로 끌어 바꾼다**(F-122) — 사양 표와 같다. 놓으면 모든 직위의 새 순서 하나를 보내고,
// 그 순서는 다음 읽기가 올 때까지 그 자리에 선다. 검색 중에는 끌 수 없다 — 보이지 않는 줄의 자리를 정할 수 없다.

type Act = { kind: 'create' } | { kind: 'remove'; ids: string[] } | { kind: 'assign' } | null

export default function QuotaTiers({ world, query }: { world: World; query: string }) {
  const tiers = usePoll(() => api.list(), POLL_MS)
  const nav = useNavigate()
  const [act, setAct] = useState<Act>(null)
  const [picked, setPicked] = useState<string[]>([])
  const [error, setError] = useState<unknown>(null)
  // 놓은 순서는 다음 읽기가 올 때까지 그 자리에 선다 — 그 읽기(`base` 와 다른 목록)가 오면 서버의 순서로 돌아간다.
  const [moved, setMoved] = useState<{ base: QuotaTier[]; ids: string[] } | null>(null)
  const served = [...(tiers.data ?? [])].sort((a, b) => a.sort_order - b.sort_order || a.display_name.localeCompare(b.display_name))
  const all = moved && moved.base === tiers.data ? moved.ids.flatMap((id) => served.filter((t) => t.id === id)) : served
  const rows = all.filter((t) => matchAny([t.display_name], query))
  const draggable = query === ''
  const reorder = async (ids: string[]) => {
    setMoved({ base: tiers.data ?? [], ids })
    setError(null)
    try {
      await api.setOrder(ids)
    } catch (e) {
      setError(e)
      setMoved(null)
    }
    tiers.refresh()
  }
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )
  const ids = rows.map((t) => t.id)
  const end = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return
    void reorder(arrayMove(ids, ids.indexOf(String(active.id)), ids.indexOf(String(over.id))))
  }

  // 고른 줄이 검색으로 사라지거나 기본이 되면 대상에서도 빠진다 — 보이지 않는 직위에 조작하지 않는다.
  const chosen = rows.filter((t) => !t.is_default && picked.includes(t.id))
  const pick = (id: string, on: boolean) => setPicked((v) => (on ? (v.includes(id) ? v : [...v, id]) : v.filter((x) => x !== id)))
  const close = () => setAct(null)
  const done = () => {
    close()
    tiers.refresh()
  }
  const removed = () => {
    setPicked([])
    done()
  }
  // 대상은 id 로 든다 — 폴링이 목록을 갈아도 옛 값을 보지 않고, 모두 사라지면 대화 상자가 닫힌다.
  const doomed = act?.kind === 'remove' ? all.filter((t) => act.ids.includes(t.id)) : []
  const asking = act?.kind === 'create' || act?.kind === 'assign' || doomed.length > 0

  return (
    <>
      <SectionCrumb />
      <PageHead
        icon={<IdCard className="size-5" />}
        title="직위"
        facts={chosen.length > 0 ? `${all.length}개 · ${chosen.length}개 선택` : `${all.length}개`}
        right={
          <div className="mt-0.5 flex flex-wrap items-center justify-end gap-1.5">
            <button type="button" className={`${ACTION} ${ACTION_PLAIN}`} onClick={() => setAct({ kind: 'assign' })}>
              직위 관리
            </button>
            <button type="button" className={`${ACTION} ${ACTION_PLAIN}`} onClick={() => setAct({ kind: 'create' })}>
              직위 생성
            </button>
            <button
              type="button"
              className={`${ACTION} ${ACTION_DANGER}`}
              disabled={chosen.length === 0}
              onClick={() => setAct({ kind: 'remove', ids: chosen.map((t) => t.id) })}
            >
              지우기
            </button>
          </div>
        }
      />

      <Section title="전체">
        <Err error={tiers.error} />
        <Err error={error} />
        {rows.length === 0 ? (
          <Empty>{query ? '검색 결과 없음' : '직위 없음'}</Empty>
        ) : (
          <div className="overflow-hidden rounded-lg border bg-card">
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={end}>
            <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            <Table className="table-fixed">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-8" />
                  <TableHead className="w-10 max-sm:hidden" />
                  <TableHead>이름</TableHead>
                  <TableHead className="w-24 max-sm:hidden">VM</TableHead>
                  <TableHead className="w-24 max-sm:hidden">CPU</TableHead>
                  <TableHead className="w-24 max-sm:hidden">메모리</TableHead>
                  <TableHead className="w-24 max-sm:hidden">디스크</TableHead>
                  <TableHead className="w-24 max-sm:hidden">노드 선택</TableHead>
                  <TableHead className="w-20 text-right">인원</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((t) => (
                  <TierRow
                    key={t.id}
                    tier={t}
                    draggable={draggable}
                    picked={picked.includes(t.id)}
                    onPick={pick}
                    onOpen={() => nav(`/quota-tiers/${t.id}`)}
                  />
                ))}
              </TableBody>
            </Table>
            </SortableContext>
            </DndContext>
          </div>
        )}
      </Section>

      <Dialog open={asking} onOpenChange={(v) => (v ? undefined : close())}>
        <DialogContent aria-describedby={undefined} className={act?.kind === 'assign' ? 'sm:max-w-3xl' : undefined}>
          {act?.kind === 'assign' ? <QuotaPanel seed={[]} teams={world.teams} onDone={close} onApplied={tiers.refresh} /> : null}
          {act?.kind === 'create' ? <TierCreateForm onDone={done} /> : null}
          {act?.kind === 'remove' && doomed.length > 0 ? (
            <TierDeleteForm tiers={doomed} onClose={close} onDone={removed} onRefused={tiers.refresh} />
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  )
}

function TierRow({
  tier: t,
  draggable,
  picked,
  onPick,
  onOpen,
}: {
  tier: QuotaTier
  draggable: boolean
  picked: boolean
  onPick: (id: string, on: boolean) => void
  onOpen: () => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: t.id, disabled: !draggable })
  return (
    <TableRow
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      onClick={onOpen}
      className={`cursor-pointer hover:bg-surface-2 ${isDragging ? 'relative z-10 bg-surface-2' : ''}`}
    >
      <TableCell onClick={(e) => e.stopPropagation()}>
        {draggable ? (
          <button
            type="button"
            aria-label={`${t.display_name} 옮기기`}
            className="flex cursor-grab touch-none items-center text-muted-foreground hover:text-foreground"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="size-4" />
          </button>
        ) : null}
      </TableCell>
      <TableCell className="max-sm:hidden" onClick={(e) => e.stopPropagation()}>
        {t.is_default ? null : (
          <Checkbox aria-label={`${t.display_name} 선택`} checked={picked} onCheckedChange={(on) => onPick(t.id, on === true)} />
        )}
      </TableCell>
      <TableCell className="font-medium max-sm:basis-full">
        <span className="flex flex-wrap items-center gap-2">
          {t.display_name}
          {t.is_default ? <ToneBadge tone="ok">기본</ToneBadge> : null}
        </span>
        <span className="mt-0.5 block text-[11px] font-normal text-muted-foreground sm:hidden">
          {limitsText(t.limits)}
          {t.node_choice ? ' · 노드 선택' : ''}
        </span>
      </TableCell>
      {limitAxes(t.limits).map(([axis, text]) => (
        <TableCell key={axis} className="text-muted-foreground tabular-nums max-sm:hidden">
          {text}
        </TableCell>
      ))}
      <TableCell className="text-muted-foreground max-sm:hidden">{t.node_choice ? '가능' : DASH}</TableCell>
      <TableCell className="text-right tabular-nums">{t.people}명</TableCell>
    </TableRow>
  )
}
